import React, { KeyboardEventHandler, useMemo, useState } from 'react'
import { Typography } from '@astrid/components'
import { Label, LabelType } from 'components/Label'
import { ShelfEmptyState } from 'components/ShelfEmptyState'
import { ShelfSection } from 'components/ShelfSection'
import {
  getShelfKey,
  getShelfSectionKey,
  getShelfTitleWithElementsCount,
  isShelfEmpty,
  ShelfSection as ShelfSectionType,
  StoreShelf,
  StoreShelfEntry
} from 'store/services/Library'
import { BookMode } from 'store/services/Book'

import styles from './Shelf.module.scss'

type ShelfProps = {
  shelf: StoreShelf
  handleItemClick: (item: StoreShelfEntry, bookMode: BookMode, shelfSource?: string) => void
  onShelfSectionEndReached: (shelfSection: ShelfSectionType) => Promise<void>
  shelfRef: (element: HTMLElement | null) => HTMLElement | null
  onKeyDown: KeyboardEventHandler<HTMLElement>
}

export const Shelf = ({ shelf, handleItemClick, shelfRef, onKeyDown, onShelfSectionEndReached }: ShelfProps) => {
  const [isCollapsed, setIsCollapsed] = useState(shelf.collapsible)

  const isEmpty = useMemo(() => isShelfEmpty(shelf), [shelf])
  const title = getShelfTitleWithElementsCount(shelf)

  const onToggleCollapsed = () => {
    setIsCollapsed((prev) => !prev)
  }

  return (
    <section className={styles.carouselContainer} aria-labelledby={getShelfKey(shelf)} onKeyDown={onKeyDown}>
      {shelf.sections.length > 1 && <div className={styles.divider} />}
      <div className={styles.horizontalContainer}>
        {shelf.accessInfo.locked && <Label type={LabelType.LOCK} large />}
        {title && (
          <Typography style={{ margin: 0, fontWeight: 900, fontSize: 24 }} variant="h2">
            {title}
          </Typography>
        )}
        {shelf.collapsible && !isEmpty && (
          <button className={styles.hideButton} onClick={onToggleCollapsed}>
            <Typography
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 600,
                color: '#3d8f74'
              }}
              variant="body"
            >
              {isCollapsed ? 'Show' : 'Hide'}
            </Typography>
          </button>
        )}
      </div>
      {isEmpty && <ShelfEmptyState shelfType={shelf.type} />}
      {!isCollapsed &&
        !isEmpty &&
        shelf.sections.map((shelfSection) => (
          <ShelfSection
            shelfRef={shelfRef}
            key={`${getShelfKey(shelf)}-${getShelfSectionKey(shelfSection)}`}
            shelf={shelf}
            shelfSection={shelfSection}
            handleItemClick={handleItemClick}
            onEndReached={onShelfSectionEndReached}
          />
        ))}
    </section>
  )
}
